/**
 * AGREEMENT API FUNCTIONS
 *
 * Frontend API calls for legal agreement management.
 *
 * CREATED: July 2025
 */

import axios from 'axios';

// =====================
// PUBLIC ENDPOINTS
// =====================

/**
 * Get all currently active agreements
 * @param {string} audience - Optional: 'seller' or 'buyer'
 */
export const getActiveAgreements = async (audience = null) => {
  const params = audience ? { audience } : {};
  return await axios.get(`${process.env.REACT_APP_API}/agreements/active`, { params });
};

/**
 * Get active agreement by type
 * @param {string} type - 'terms_of_service', 'privacy_policy', 'seller_agreement', 'buyer_agreement'
 */
export const getAgreementByType = async (type) => {
  return await axios.get(`${process.env.REACT_APP_API}/agreements/type/${type}`);
};

// =====================
// USER ENDPOINTS
// =====================

/**
 * Get pending agreements that user needs to accept
 * @param {string} authtoken
 */
export const getPendingAgreements = async (authtoken) => {
  return await axios.get(`${process.env.REACT_APP_API}/agreements/pending`, {
    headers: { authtoken }
  });
};

/**
 * Check if user has accepted all required agreements
 * @param {string} authtoken
 */
export const checkAcceptanceStatus = async (authtoken) => {
  return await axios.get(`${process.env.REACT_APP_API}/agreements/check-status`, {
    headers: { authtoken }
  });
};

/**
 * Get user's agreement acceptance history
 * @param {string} authtoken
 */
export const getAcceptanceHistory = async (authtoken) => {
  return await axios.get(`${process.env.REACT_APP_API}/agreements/history`, {
    headers: { authtoken }
  });
};

/**
 * Accept a single agreement
 * @param {string} agreementId
 * @param {string} authtoken
 * @param {string} businessId - Optional, for seller agreements
 */
export const acceptAgreement = async (agreementId, authtoken, businessId = null) => {
  return await axios.post(
    `${process.env.REACT_APP_API}/agreements/${agreementId}/accept`,
    { businessId },
    { headers: { authtoken } }
  );
};

/**
 * Accept multiple agreements at once
 * @param {string[]} agreementIds
 * @param {string} authtoken
 * @param {string} businessId - Optional, for seller agreements
 */
export const acceptMultipleAgreements = async (agreementIds, authtoken, businessId = null) => {
  return await axios.post(
    `${process.env.REACT_APP_API}/agreements/accept-multiple`,
    { agreementIds, businessId },
    { headers: { authtoken } }
  );
};

// =====================
// ADMIN ENDPOINTS
// =====================

/**
 * Create a new agreement (admin only)
 * @param {object} data - { type, title, content, contentFormat, requiresAcceptance, targetAudience, gracePeriodDays }
 * @param {string} authtoken
 */
export const createAgreement = async (data, authtoken) => {
  return await axios.post(`${process.env.REACT_APP_API}/admin/agreements`, data, {
    headers: { authtoken }
  });
};

/**
 * Update an agreement (admin only)
 * @param {string} agreementId
 * @param {object} data
 * @param {string} authtoken
 */
export const updateAgreement = async (agreementId, data, authtoken) => {
  return await axios.put(`${process.env.REACT_APP_API}/admin/agreements/${agreementId}`, data, {
    headers: { authtoken }
  });
};

/**
 * Publish an agreement (admin only)
 * @param {string} agreementId
 * @param {string} authtoken
 */
export const publishAgreement = async (agreementId, authtoken) => {
  return await axios.post(`${process.env.REACT_APP_API}/admin/agreements/${agreementId}/publish`, {}, {
    headers: { authtoken }
  });
};

/**
 * Get all agreements (admin only)
 * @param {string} authtoken
 * @param {object} filters - { type, activeOnly }
 */
export const getAllAgreements = async (authtoken, filters = {}) => {
  return await axios.get(`${process.env.REACT_APP_API}/admin/agreements`, {
    headers: { authtoken },
    params: filters
  });
};

/**
 * Get single agreement (admin only)
 * @param {string} agreementId
 * @param {string} authtoken
 */
export const getAgreement = async (agreementId, authtoken) => {
  return await axios.get(`${process.env.REACT_APP_API}/admin/agreements/${agreementId}`, {
    headers: { authtoken }
  });
};

/**
 * Delete draft agreement (admin only)
 * @param {string} agreementId
 * @param {string} authtoken
 */
export const deleteAgreement = async (agreementId, authtoken) => {
  return await axios.delete(`${process.env.REACT_APP_API}/admin/agreements/${agreementId}`, {
    headers: { authtoken }
  });
};

/**
 * Get agreement statistics (admin only)
 * @param {string} agreementId
 * @param {string} authtoken
 */
export const getAgreementStats = async (agreementId, authtoken) => {
  return await axios.get(`${process.env.REACT_APP_API}/admin/agreements/${agreementId}/stats`, {
    headers: { authtoken }
  });
};
